<template>
  <div class="guest theme-group-3">
    <app-page-wrapper-grid class="registration__grid">
      <!-- Back button -->
      <div class="iw-layout-grid__row p-l-16 p-b-8 p-t-24">
        <div class="iw-layout-grid__col--span-12">
          <back-button />
        </div>
      </div>

      <!-- Form -->
      <div class="iw-layout-grid__row p-16">
        <div class="iw-layout-grid__col--span-4 iw-layout-grid__col--offset-4-desktop">
          <form id="guestForm" class="guest__form">
            <h3 v-if="isLoginAllowed" class="iw-page-title m-b-36">Continue as Guest</h3>
            <template v-if="isFormSubmitted && v$.$invalid">
              <app-notifier
                v-for="(message, index) in errors"
                :key="index"
                class="guest__error"
                :type="'failed'"
                :data="message"
              />
            </template>
            <div class="iw-body-text-small m-l-auto">*Required Information</div>
            <app-input
              :id="'guest-name'"
              v-model="v$.guest.name.$model"
              class="guest__input"
              :label="'Full Name'"
              :has-error="v$.guest.name.$error && isFormSubmitted"
              required
            />
            <app-input
              :id="'guest-phone'"
              v-model="v$.guest.phone.$model"
              :label="'Phone'"
              mask="(###) ###-####"
              :has-error="v$.guest.phone.$error && isFormSubmitted"
              :error-message="v$.guest.phone.$error && isFormSubmitted ? validatorsCollection.get('phone').errorMessage : ''"
              required
            />
            <email-guidance-text class="m-t-5 m-b-15" />
            <app-input
              :id="'guest-email'"
              v-model="v$.guest.email.$model"
              :label="'Email'"
              :has-error="v$.guest.email.$error && isFormSubmitted"
              :error-message="v$.guest.email.$error && isFormSubmitted ? validatorsCollection.get('email').errorMessage : ''"
              required
            />
            <p class="iw-body-text-small guest__policy-text">
              Information captured will only be used for updates to your order. By entering your phone number here you agree to
              receive SMS order updates. For more information see our
              <app-link class="guest__tclink" :route-to="{}" @click="openPrivacyPolicyModal">Privacy Policy</app-link>
            </p>
            <app-button class="m-b-30 m-t-0" :text="'Continue'" :aria-label="'Continue'" :handler="registerGuest" />
            <div v-if="isLoginAllowed" class="iw-body-text-small m-t-0 m-b-32">
              <span
                >Already have an account?<app-link :route-to="{ name: ROUTE_NAMES.LOGIN }" class="login__link m-l-10"
                  >Login</app-link
                ></span
              >
            </div>
          </form>
        </div>
      </div>
    </app-page-wrapper-grid>
  </div>
</template>

<script>
/* eslint-disable vue/no-unused-components */
import { mapGetters, mapActions } from "vuex";
import useVuelidate from "@vuelidate/core";
import { requiredValidation, emailValidation, phoneValidations, ROUTE_NAMES, MIN_DESKTOP_WIDTH, REQUIRED_ERR_MSG } from "@/utils";
import PrivacyPolicyModal from "@/components/modal/privacy-policy-modal/PrivacyPolicyModal";
import BackButton from "@/components/buttons/back-button/BackButton";
import EmailGuidanceText from "@/components/messages/EmailGuidanceText.vue";

export default {
  name: "ContinueAsGuest",
  components: {
    PrivacyPolicyModal,
    BackButton,
    EmailGuidanceText
  },
  setup: () => ({ v$: useVuelidate() }),
  data() {
    return {
      guest: {
        name: "",
        email: "",
        phone: ""
      },
      isFormSubmitted: false,
      errors: [],
      validatorsCollection: new Map(),
      ROUTE_NAMES
    };
  },
  validations: {
    guest: {
      name: requiredValidation(),
      email: emailValidation(),
      phone: phoneValidations()
    }
  },
  computed: {
    ...mapGetters("user", ["getUserData"]),
    ...mapGetters("venueSettings", ["isLoginAllowed"])
  },
  mounted() {
    this.validatorsCollection
      .set("name", { validator: this.v$.guest.name, errorMessage: "" })
      .set("email", {
        validator: this.v$.guest.email,
        errorMessage: "Please enter a valid email address"
      })
      .set("phone", {
        validator: this.v$.guest.phone,
        errorMessage: "Please enter a valid 10-digit mobile number"
      });
  },
  methods: {
    ...mapActions("user", ["createGuestUser"]),
    registerGuest() {
      this.isFormSubmitted = false;
      this.errors = [];

      this.v$.$touch();
      this.checkIncompleteFields();
      this.checkInvalidFields();
      this.isFormSubmitted = true;

      if (!this.v$.$invalid) {
        return this.createGuestUser(this.guest, true)
          .then(() => {
            if (this.$route.params.target) {
              this.$router.push({ name: this.$route.params.target, params: this.$route.params });
            } else {
              this.$router.push({ name: ROUTE_NAMES.CHECKOUT, params: this.$route.params });
            }
          })
          .catch((reject) => this.errors.push(reject.data.message));
      }
    },
    checkIncompleteFields() {
      let emptyFieldsExist = false;

      this.validatorsCollection.forEach((item) => {
        emptyFieldsExist = emptyFieldsExist || item.validator.required.$invalid;
      });

      if (emptyFieldsExist) {
        this.errors.push(REQUIRED_ERR_MSG);
      }
    },
    checkInvalidFields() {
      this.validatorsCollection.forEach((item, key) => {
        if (item.validator.$invalid && item.errorMessage) {
          if (key !== "phone" && key !== "email") {
            // <- IWA-2043: remove check when all fields are treated the same
            this.errors.push(item.errorMessage);
          }
        }
      });
    },
    openPrivacyPolicyModal() {
      this.$modal.show(
        PrivacyPolicyModal,
        {},
        window.innerWidth > MIN_DESKTOP_WIDTH
          ? {
              name: "PrivacyPolicyModal",
              height: "85%",
              width: "70%",
              adaptive: true,
              scrollable: true
            }
          : {
              name: "PrivacyPolicyModal",
              height: "100%",
              width: "100%",
              adaptive: true,
              scrollable: true
            }
      );
    }
  }
};
</script>

<style lang="scss">
.guest {
  &__form {
    display: flex;
    flex-direction: column;
    justify-content: left;
    align-items: flex-start;
  }

  &__submit {
    margin-top: 20px;
    height: 50px;
  }

  &__error {
    width: 100%;
    box-sizing: border-box;
    margin-bottom: 10px;
    padding: 10px 0 10px 20px;
    justify-content: flex-start;
    text-transform: none;
    font-family: $fontFamilyPrimary !important;

    @include respond-to-mobile {
      flex-direction: row;
      height: initial;
      padding: 10px 20px;
    }
  }

  &__remember {
    text-transform: uppercase;
  }

  &__tclink {
    &:hover {
      text-decoration: underline;
    }
  }

  &__policy-text {
    color: $group-3-color-3 !important;
    margin: 4px 16px 24px 16px;
  }

  .input {
    margin-bottom: 20px !important;
  }
}
</style>
